/**
 * WebGL2 Fallback Compute Engine for Helios
 * Provides GPU-accelerated computation using WebGL2 when WebGPU is unavailable.
 * Uses texture-based GPGPU (General-Purpose GPU) techniques.
 */

import type { PositionedNode, ClusterResult } from './webgpu-engine';

export interface WebGLCapabilities {
  backend: 'webgl';
  maxTextureSize: number;
  maxRenderbufferSize: number;
  renderer: string;
  vendor: string;
}

// Vertex shader for passthrough (used for GPGPU)
const PASSTHROUGH_VS = `#version 300 es
in vec2 a_position;
out vec2 v_texCoord;
void main() {
  v_texCoord = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// Fragment shader for force layout step
const FORCE_LAYOUT_FS = `#version 300 es
precision highp float;

uniform sampler2D u_positions;
uniform sampler2D u_velocities;
uniform sampler2D u_edges;
uniform int u_nodeCount;
uniform int u_edgeCount;
uniform float u_repulsion;
uniform float u_attraction;
uniform float u_damping;
uniform float u_deltaT;
uniform float u_gravity;
uniform int u_texWidth;
uniform int u_pass; // 0 = compute forces, 1 = update positions

in vec2 v_texCoord;
out vec4 fragColor;

vec2 getPosition(int idx) {
  int x = idx % u_texWidth;
  int y = idx / u_texWidth;
  return texelFetch(u_positions, ivec2(x, y), 0).xy;
}

vec2 getVelocity(int idx) {
  int x = idx % u_texWidth;
  int y = idx / u_texWidth;
  return texelFetch(u_velocities, ivec2(x, y), 0).xy;
}

vec2 getEdge(int idx) {
  int x = idx % u_texWidth;
  int y = idx / u_texWidth;
  return texelFetch(u_edges, ivec2(x, y), 0).xy;
}

void main() {
  int idx = int(gl_FragCoord.x) + int(gl_FragCoord.y) * u_texWidth;
  if (idx >= u_nodeCount) {
    fragColor = vec4(0.0);
    return;
  }

  vec2 myPos = getPosition(idx);

  if (u_pass == 0) {
    // Compute forces -> output new velocity
    vec2 force = vec2(0.0);

    // Repulsion
    for (int j = 0; j < 4096; j++) {
      if (j >= u_nodeCount) break;
      if (j == idx) continue;
      vec2 otherPos = getPosition(j);
      vec2 delta = myPos - otherPos;
      float distSq = max(dot(delta, delta), 0.01);
      float dist = sqrt(distSq);
      force += (u_repulsion / distSq) * (delta / dist);
    }

    // Attraction along edges
    for (int e = 0; e < 8192; e++) {
      if (e >= u_edgeCount) break;
      vec2 edge = getEdge(e);
      int src = int(edge.x);
      int tgt = int(edge.y);
      int neighborIdx = -1;
      if (src == idx) neighborIdx = tgt;
      else if (tgt == idx) neighborIdx = src;
      if (neighborIdx >= 0) {
        vec2 neighborPos = getPosition(neighborIdx);
        vec2 delta = neighborPos - myPos;
        float dist = max(length(delta), 0.01);
        force += u_attraction * (delta / dist) * (dist - 50.0);
      }
    }

    // Gravity
    float centerDist = max(length(myPos), 0.01);
    force += u_gravity * (-myPos / centerDist) * centerDist;

    // Update velocity
    vec2 vel = getVelocity(idx);
    vel = (vel + force * u_deltaT) * u_damping;
    float speed = length(vel);
    if (speed > 50.0) vel *= 50.0 / speed;

    fragColor = vec4(vel, 0.0, 1.0);
  } else {
    // Update position
    vec2 vel = getVelocity(idx);
    fragColor = vec4(myPos + vel * u_deltaT, 0.0, 1.0);
  }
}
`;

// Fragment shader for cosine similarity
const SIMILARITY_FS = `#version 300 es
precision highp float;

uniform sampler2D u_vectorsA;
uniform sampler2D u_vectorsB;
uniform int u_rowsA;
uniform int u_rowsB;
uniform int u_dim;
uniform int u_texWidth;

in vec2 v_texCoord;
out vec4 fragColor;

void main() {
  int flatIdx = int(gl_FragCoord.x) + int(gl_FragCoord.y) * u_texWidth;
  int i = flatIdx / u_rowsB;
  int j = flatIdx % u_rowsB;

  if (i >= u_rowsA || j >= u_rowsB) {
    fragColor = vec4(0.0);
    return;
  }

  float dotProduct = 0.0;
  float normA = 0.0;
  float normB = 0.0;

  for (int d = 0; d < 2048; d++) {
    if (d >= u_dim) break;
    int aIdx = i * u_dim + d;
    int bIdx = j * u_dim + d;
    float a = texelFetch(u_vectorsA, ivec2(aIdx % u_texWidth, aIdx / u_texWidth), 0).r;
    float b = texelFetch(u_vectorsB, ivec2(bIdx % u_texWidth, bIdx / u_texWidth), 0).r;
    dotProduct += a * b;
    normA += a * a;
    normB += b * b;
  }

  float denom = sqrt(normA) * sqrt(normB);
  float similarity = denom > 0.0001 ? dotProduct / denom : 0.0;
  fragColor = vec4(similarity, 0.0, 0.0, 1.0);
}
`;

export class WebGLFallback {
  private gl: WebGL2RenderingContext | null = null;
  private canvas: OffscreenCanvas | HTMLCanvasElement | null = null;
  private quadVAO: WebGLVertexArrayObject | null = null;

  async init(): Promise<void> {
    // Use OffscreenCanvas if available, otherwise create a hidden canvas
    if (typeof OffscreenCanvas !== 'undefined') {
      this.canvas = new OffscreenCanvas(1, 1);
    } else {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      canvas.style.display = 'none';
      document.body.appendChild(canvas);
      this.canvas = canvas;
    }

    this.gl = this.canvas.getContext('webgl2', {
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    }) as WebGL2RenderingContext;

    if (!this.gl) {
      throw new Error('WebGL2 not supported');
    }

    // Check for float texture support
    const ext = this.gl.getExtension('EXT_color_buffer_float');
    if (!ext) {
      throw new Error('EXT_color_buffer_float not supported');
    }

    // Create fullscreen quad VAO
    this.quadVAO = this.gl.createVertexArray();
    this.gl.bindVertexArray(this.quadVAO);

    const quadBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, quadBuffer);
    this.gl.bufferData(
      this.gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      this.gl.STATIC_DRAW
    );
    this.gl.enableVertexAttribArray(0);
    this.gl.vertexAttribPointer(0, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.bindVertexArray(null);

    console.log('[WebGL2] Fallback engine initialized');
  }

  private compileShader(source: string, type: number): WebGLShader {
    const gl = this.gl!;
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const info = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Shader compilation failed: ${info}`);
    }
    return shader;
  }

  private createProgram(vsSource: string, fsSource: string): WebGLProgram {
    const gl = this.gl!;
    const vs = this.compileShader(vsSource, gl.VERTEX_SHADER);
    const fs = this.compileShader(fsSource, gl.FRAGMENT_SHADER);
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`Program linking failed: ${info}`);
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return program;
  }

  private createFloat32Texture(data: Float32Array, width: number, height: number): WebGLTexture {
    const gl = this.gl!;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, width, height, 0, gl.RGBA, gl.FLOAT, data);
    return tex;
  }

  private createFramebuffer(
    texture: WebGLTexture,
    width: number,
    height: number
  ): WebGLFramebuffer {
    const gl = this.gl!;
    const fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.viewport(0, 0, width, height);
    return fb;
  }

  private readPixelsFloat(fb: WebGLFramebuffer, width: number, height: number): Float32Array {
    const gl = this.gl!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    const result = new Float32Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.FLOAT, result);
    return result;
  }

  async computeForceLayout(
    nodes: Array<{ id: string; x?: number; y?: number; mass?: number }>,
    edges: Array<{ source: number; target: number }>,
    iterations: number = 100,
    options?: {
      repulsion?: number;
      attraction?: number;
      damping?: number;
      gravity?: number;
    }
  ): Promise<PositionedNode[]> {
    if (!this.gl) throw new Error('Engine not initialized');

    const nodeCount = nodes.length;
    if (nodeCount === 0) return [];

    const texWidth = Math.ceil(Math.sqrt(nodeCount));
    const texHeight = texWidth;
    const texSize = texWidth * texHeight;

    // Initialize positions
    const posData = new Float32Array(texSize * 4);
    for (let i = 0; i < nodeCount; i++) {
      if (nodes[i].x !== undefined && nodes[i].y !== undefined) {
        posData[i * 4 + 0] = nodes[i].x!;
        posData[i * 4 + 1] = nodes[i].y!;
      } else {
        const angle = (2 * Math.PI * i) / nodeCount;
        const radius = Math.sqrt(nodeCount) * 15;
        posData[i * 4 + 0] = Math.cos(angle) * radius;
        posData[i * 4 + 1] = Math.sin(angle) * radius;
      }
    }

    // Initialize velocities
    const velData = new Float32Array(texSize * 4);

    // Prepare edges
    const edgeTexData = new Float32Array(texSize * 4);
    for (let i = 0; i < edges.length && i < texSize; i++) {
      edgeTexData[i * 4 + 0] = edges[i].source;
      edgeTexData[i * 4 + 1] = edges[i].target;
    }

    const gl = this.gl;

    const program = this.createProgram(PASSTHROUGH_VS, FORCE_LAYOUT_FS);
    gl.useProgram(program);

    // Set uniforms
    gl.uniform1i(gl.getUniformLocation(program, 'u_nodeCount'), nodeCount);
    gl.uniform1i(gl.getUniformLocation(program, 'u_edgeCount'), edges.length);
    gl.uniform1f(gl.getUniformLocation(program, 'u_repulsion'), options?.repulsion ?? 500.0);
    gl.uniform1f(gl.getUniformLocation(program, 'u_attraction'), options?.attraction ?? 0.01);
    gl.uniform1f(gl.getUniformLocation(program, 'u_damping'), options?.damping ?? 0.9);
    gl.uniform1f(gl.getUniformLocation(program, 'u_deltaT'), 0.1);
    gl.uniform1f(gl.getUniformLocation(program, 'u_gravity'), options?.gravity ?? 0.1);
    gl.uniform1i(gl.getUniformLocation(program, 'u_texWidth'), texWidth);

    // Create textures
    let posTex = this.createFloat32Texture(posData, texWidth, texHeight);
    let velTex = this.createFloat32Texture(velData, texWidth, texHeight);
    const edgeTex = this.createFloat32Texture(edgeTexData, texWidth, texHeight);

    // Output textures (double-buffer)
    const velOutData = new Float32Array(texSize * 4);
    let velOutTex = this.createFloat32Texture(velOutData, texWidth, texHeight);
    const posOutData = new Float32Array(texSize * 4);
    let posOutTex = this.createFloat32Texture(posOutData, texWidth, texHeight);

    this.canvas!.width = texWidth;
    this.canvas!.height = texHeight;

    for (let iter = 0; iter < iterations; iter++) {
      gl.useProgram(program);
      gl.bindVertexArray(this.quadVAO);

      // Pass 0: compute velocities
      gl.uniform1i(gl.getUniformLocation(program, 'u_pass'), 0);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, posTex);
      gl.uniform1i(gl.getUniformLocation(program, 'u_positions'), 0);

      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, velTex);
      gl.uniform1i(gl.getUniformLocation(program, 'u_velocities'), 1);

      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, edgeTex);
      gl.uniform1i(gl.getUniformLocation(program, 'u_edges'), 2);

      const velFb = this.createFramebuffer(velOutTex, texWidth, texHeight);
      gl.viewport(0, 0, texWidth, texHeight);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      // Pass 1: update positions
      gl.uniform1i(gl.getUniformLocation(program, 'u_pass'), 1);

      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, velOutTex);

      const posFb = this.createFramebuffer(posOutTex, texWidth, texHeight);
      gl.viewport(0, 0, texWidth, texHeight);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      // Swap buffers
      [posTex, posOutTex] = [posOutTex, posTex];
      [velTex, velOutTex] = [velOutTex, velTex];

      gl.deleteFramebuffer(velFb);
      gl.deleteFramebuffer(posFb);
    }

    // Read final positions
    const finalOutTex = this.createFloat32Texture(new Float32Array(texSize * 4), texWidth, texHeight);
    const readFb = this.createFramebuffer(posTex, texWidth, texHeight);
    const finalPositions = this.readPixelsFloat(readFb, texWidth, texHeight);

    // Cleanup
    gl.deleteTexture(posTex);
    gl.deleteTexture(posOutTex);
    gl.deleteTexture(velTex);
    gl.deleteTexture(velOutTex);
    gl.deleteTexture(edgeTex);
    gl.deleteTexture(finalOutTex);
    gl.deleteFramebuffer(readFb);
    gl.deleteProgram(program);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    return nodes.map((n, i) => ({
      id: n.id,
      x: finalPositions[i * 4 + 0],
      y: finalPositions[i * 4 + 1],
    }));
  }

  async computeEmbeddingSimilarity(
    vectorsA: Float32Array,
    vectorsB: Float32Array,
    dim: number
  ): Promise<Float32Array> {
    if (!this.gl) throw new Error('Engine not initialized');

    const rowsA = vectorsA.length / dim;
    const rowsB = vectorsB.length / dim;
    const resultSize = rowsA * rowsB;

    const gl = this.gl;
    const texWidth = Math.ceil(Math.sqrt(Math.max(vectorsA.length, vectorsB.length, resultSize)));
    const texHeight = texWidth;
    const texSize = texWidth * texHeight;

    // Pad vectors into RGBA texture format
    const aData = new Float32Array(texSize * 4);
    for (let i = 0; i < vectorsA.length; i++) {
      aData[i * 4] = vectorsA[i];
    }
    const bData = new Float32Array(texSize * 4);
    for (let i = 0; i < vectorsB.length; i++) {
      bData[i * 4] = vectorsB[i];
    }

    const program = this.createProgram(PASSTHROUGH_VS, SIMILARITY_FS);
    gl.useProgram(program);

    gl.uniform1i(gl.getUniformLocation(program, 'u_rowsA'), rowsA);
    gl.uniform1i(gl.getUniformLocation(program, 'u_rowsB'), rowsB);
    gl.uniform1i(gl.getUniformLocation(program, 'u_dim'), dim);
    gl.uniform1i(gl.getUniformLocation(program, 'u_texWidth'), texWidth);

    const aTex = this.createFloat32Texture(aData, texWidth, texHeight);
    const bTex = this.createFloat32Texture(bData, texWidth, texHeight);
    const outTex = this.createFloat32Texture(new Float32Array(texSize * 4), texWidth, texHeight);

    this.canvas!.width = texWidth;
    this.canvas!.height = texHeight;

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, aTex);
    gl.uniform1i(gl.getUniformLocation(program, 'u_vectorsA'), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, bTex);
    gl.uniform1i(gl.getUniformLocation(program, 'u_vectorsB'), 1);

    gl.bindVertexArray(this.quadVAO);
    const fb = this.createFramebuffer(outTex, texWidth, texHeight);
    gl.viewport(0, 0, texWidth, texHeight);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    const rawResult = this.readPixelsFloat(fb, texWidth, texHeight);

    // Extract similarity values (stored in R channel)
    const result = new Float32Array(resultSize);
    for (let i = 0; i < resultSize; i++) {
      result[i] = rawResult[i * 4];
    }

    gl.deleteTexture(aTex);
    gl.deleteTexture(bTex);
    gl.deleteTexture(outTex);
    gl.deleteFramebuffer(fb);
    gl.deleteProgram(program);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    return result;
  }

  async computeHeatmap(
    data: Float32Array,
    width: number,
    height: number
  ): Promise<Uint8ClampedArray> {
    // CPU heatmap generation (WebGL heatmap would need a dedicated shader)
    const pixelCount = width * height;
    let minVal = Infinity;
    let maxVal = -Infinity;
    for (let i = 0; i < data.length; i++) {
      if (data[i] < minVal) minVal = data[i];
      if (data[i] > maxVal) maxVal = data[i];
    }

    const range = maxVal - minVal;
    const rgba = new Uint8ClampedArray(pixelCount * 4);

    for (let i = 0; i < pixelCount; i++) {
      const t = range > 0.0001 ? (data[i] - minVal) / range : 0;
      // Viridis-like colormap
      const r = Math.max(0, Math.min(1, 1.5 - Math.abs(t * 4 - 3)));
      const g = Math.max(0, Math.min(1, 1.5 - Math.abs(t * 4 - 2)));
      const b = Math.max(0, Math.min(1, 1.5 - Math.abs(t * 4 - 1)));
      rgba[i * 4 + 0] = r * 255;
      rgba[i * 4 + 1] = g * 255;
      rgba[i * 4 + 2] = b * 255;
      rgba[i * 4 + 3] = 255;
    }

    return rgba;
  }

  async computeClusterDetection(
    adjacencyMatrix: Float32Array,
    numClusters: number = 4
  ): Promise<ClusterResult[]> {
    // CPU-based spectral clustering (same algorithm as WebGPU engine)
    const n = Math.round(Math.sqrt(adjacencyMatrix.length));

    const degree = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let j = 0; j < n; j++) sum += adjacencyMatrix[i * n + j];
      degree[i] = sum;
    }

    const laplacian = new Float32Array(n * n);
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) {
          laplacian[i * n + j] = degree[i] > 0 ? 1.0 : 0.0;
        } else {
          const dI = degree[i] > 0 ? 1 / Math.sqrt(degree[i]) : 0;
          const dJ = degree[j] > 0 ? 1 / Math.sqrt(degree[j]) : 0;
          laplacian[i * n + j] = -adjacencyMatrix[i * n + j] * dI * dJ;
        }
      }
    }

    const eigenvectors: Float32Array[] = [];
    for (let k = 0; k < Math.min(numClusters, n); k++) {
      let vec = new Float32Array(n);
      for (let i = 0; i < n; i++) vec[i] = Math.random() - 0.5;

      for (const prev of eigenvectors) {
        let dot = 0;
        for (let i = 0; i < n; i++) dot += vec[i] * prev[i];
        for (let i = 0; i < n; i++) vec[i] -= dot * prev[i];
      }

      for (let iter = 0; iter < 20; iter++) {
        const result = new Float32Array(n);
        for (let i = 0; i < n; i++) {
          let sum = 0;
          for (let j = 0; j < n; j++) sum += laplacian[i * n + j] * vec[j];
          result[i] = sum;
        }

        for (const prev of eigenvectors) {
          let dot = 0;
          for (let i = 0; i < n; i++) dot += result[i] * prev[i];
          for (let i = 0; i < n; i++) result[i] -= dot * prev[i];
        }

        let norm = 0;
        for (let i = 0; i < n; i++) norm += result[i] * result[i];
        norm = Math.sqrt(norm);
        if (norm > 1e-10) for (let i = 0; i < n; i++) result[i] /= norm;

        vec = result;
      }
      eigenvectors.push(vec);
    }

    const clusters: ClusterResult[] = [];
    if (eigenvectors.length >= 2) {
      const embedding = eigenvectors.slice(0, numClusters);
      const centroids: number[][] = [];
      for (let k = 0; k < numClusters; k++) {
        const idx = Math.floor((k * n) / numClusters);
        centroids.push(embedding.map((ev) => ev[idx]));
      }

      const assignments = new Int32Array(n);
      for (let iter = 0; iter < 20; iter++) {
        for (let i = 0; i < n; i++) {
          let bestDist = Infinity;
          let bestK = 0;
          for (let k = 0; k < numClusters; k++) {
            let dist = 0;
            for (let d = 0; d < embedding.length; d++) {
              const diff = embedding[d][i] - centroids[k][d];
              dist += diff * diff;
            }
            if (dist < bestDist) { bestDist = dist; bestK = k; }
          }
          assignments[i] = bestK;
        }
        for (let k = 0; k < numClusters; k++) {
          const count = assignments.filter((a) => a === k).length;
          if (count === 0) continue;
          for (let d = 0; d < embedding.length; d++) {
            let sum = 0;
            for (let i = 0; i < n; i++) if (assignments[i] === k) sum += embedding[d][i];
            centroids[k][d] = sum / count;
          }
        }
      }
      for (let i = 0; i < n; i++) clusters.push({ nodeIndex: i, clusterId: assignments[i] });
    } else {
      for (let i = 0; i < n; i++) clusters.push({ nodeIndex: i, clusterId: 0 });
    }

    return clusters;
  }

  getCapabilities(): WebGLCapabilities | null {
    if (!this.gl) return null;
    const gl = this.gl;
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      backend: 'webgl',
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      renderer: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : 'Unknown',
      vendor: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : 'Unknown',
    };
  }

  dispose(): void {
    if (this.gl) {
      const ext = this.gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    }
    if (this.canvas && this.canvas instanceof HTMLCanvasElement) {
      this.canvas.remove();
    }
    this.gl = null;
    this.canvas = null;
    this.quadVAO = null;
    console.log('[WebGL2] Fallback engine disposed');
  }
}
